/**
 * `@zep/logger/react` — React 트래킹 진입점 (react peerDep).
 *
 * - `useImpressionRef()` : 노출(보일 때 1회) IntersectionObserver 훅.
 * - `createDomainTracking(useCommonProps)` : 도메인 공통 prop 을 **바인딩**해
 *   `useTrack` / `<TrackImpression>` / `<TrackClick>` 을 만든다.
 *
 * 공통 prop 은 Provider 가 아니라 `createDomainTracking` 으로 **생성 시점에 바인딩**된다.
 * → 컴포넌트를 만들려면 반드시 공통 prop 훅을 넘겨야 하므로, "Provider 밖이라 공통 prop 0" 같은
 *   조용한 누락이 구조적으로 불가능하다.
 *
 * 이름/prop 은 전부 `EventMap`(도메인이 `declare module` 로 증강) 으로 컴파일 강제.
 * JSX 없이 `createElement` 로 작성 — 빌드(vp pack) JSX 설정에 의존하지 않는다.
 */
import type { MouseEvent, ReactElement, ReactNode } from 'react';
import {
  cloneElement,
  createElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import type { EventMap, OtelSafeValue } from '../logger/types';
import { createEventTracker } from '../tracking';

// ─── 노출(impression) ──────────────────────────────────────────────────────────

type ImpressionOptions = {
  /** 요소가 이만큼 보이면 노출로 침 (0~1). 기본 0.5 */
  threshold?: number;
  /** 1회만 발화 후 관측 해제. 기본 true */
  once?: boolean;
  rootMargin?: string;
};

/**
 * "요소가 실제로 보일 때"(마운트 아님) 1회 콜백하는 순수 IntersectionObserver 훅.
 * SSR/미지원 환경에서는 no-op.
 */
export function useImpressionRef<T extends HTMLElement = HTMLElement>(
  onImpress: () => void,
  { threshold = 0.5, once = true, rootMargin }: ImpressionOptions = {},
) {
  const ref = useRef<T>(null);
  const firedRef = useRef(false);
  const cbRef = useRef(onImpress);
  cbRef.current = onImpress;

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (once && firedRef.current) return;
          firedRef.current = true;
          cbRef.current();
          if (once) io.disconnect();
        }
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, once, rootMargin]);

  return ref;
}

// ─── 도메인 트래킹 팩토리 ───────────────────────────────────────────────────────

type TrackImpressionProps<K extends keyof EventMap & string> = {
  /** EventMap 에 등록된 노출 이벤트명 (타입 강제) */
  event: K;
  /** 해당 이벤트의 prop (타입 강제). 공통 prop 은 팩토리가 바인딩 */
  props: EventMap[K];
  children: ReactNode;
  /** 0~1, 기본 0.5 */
  threshold?: number;
  /** 1회만, 기본 true */
  once?: boolean;
  className?: string;
};

type ClickableChild = ReactElement<{
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}>;

type TrackClickProps<K extends keyof EventMap & string> = {
  /** EventMap 에 등록된 클릭 이벤트명 (타입 강제) */
  event: K;
  /** 해당 이벤트의 prop (타입 강제). 공통 prop 은 팩토리가 바인딩 */
  props: EventMap[K];
  /** onClick 을 받을 수 있는 단일 요소/컴포넌트 */
  children: ClickableChild;
};

/**
 * 도메인 공통 prop 훅을 바인딩해 트래킹 진입점 3종을 만든다.
 * 공통 prop(탭/권한/place 등)이 생성 시점에 묶이므로 Provider 가 필요 없고, 누락도 불가능하다.
 *
 *   // 도메인 모듈에서 (예: play_setting)
 *   export const { useTrack: useSettingTrack, TrackClick, TrackImpression } =
 *     createDomainTracking(useSettingTrackProps);
 */
export function createDomainTracking(
  useCommonProps: () => Record<string, OtelSafeValue>,
) {
  /** 명령형 — 토글/전이/저장 등 클릭 시점 계산값. */
  function useTrack() {
    const context = useCommonProps();
    const track = useMemo(() => createEventTracker(() => context), [context]);
    return { track };
  }

  /** 노출 — 자식이 실제로 보일 때 1회 발화. */
  function TrackImpression<K extends keyof EventMap & string>({
    event,
    props,
    children,
    threshold,
    once,
    className,
  }: TrackImpressionProps<K>) {
    const { track } = useTrack();
    const ref = useImpressionRef<HTMLDivElement>(() => track(event, props), {
      threshold,
      once,
    });
    return createElement('div', { ref, className }, children);
  }

  /** 정적 클릭 — 자식을 감싸 클릭 시 발화. 기존 onClick 보존(먼저 실행). DOM 추가 없음. */
  function TrackClick<K extends keyof EventMap & string>({
    event,
    props,
    children,
  }: TrackClickProps<K>) {
    const { track } = useTrack();
    if (!isValidElement(children)) return children;
    return cloneElement(children, {
      onClick: (e: MouseEvent<HTMLElement>) => {
        children.props.onClick?.(e);
        track(event, props);
      },
    });
  }

  return { useTrack, TrackImpression, TrackClick };
}
